import { useState, useRef, useEffect, useCallback } from 'react';

interface UseAudioPlayerReturn {
  /** Load a URL and start playing. If same URL is already loaded, resumes. */
  play: (url: string) => void;
  /** Pause current playback. */
  pause: () => void;
  /** Toggle play/pause on current audio. */
  toggle: () => void;
  /** Stop playback and reset position to 0. */
  stop: () => void;
  /** Seek to a specific time (seconds). */
  seek: (time: number) => void;
  /** Whether audio is currently playing. */
  isPlaying: boolean;
  /** Current playback position in seconds. */
  currentTime: number;
  /** Total duration in seconds (0 until loaded). */
  duration: number;
  /** The URL currently loaded, or null. */
  currentSrc: string | null;
}

/**
 * Shared hook for audio playback using the HTML5 Audio API.
 * Used by AudioPlayer component (seek bar, timer) and AudioAssetsList (simple play/pause).
 */
export function useAudioPlayer(onEnd?: () => void): UseAudioPlayerReturn {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const onEndRef = useRef(onEnd);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentSrc, setCurrentSrc] = useState<string | null>(null);

  // Keep onEnd callback ref fresh without re-creating listeners
  useEffect(() => {
    onEndRef.current = onEnd;
  }, [onEnd]);

  // Poll currentTime while playing
  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      if (audioRef.current) {
        setCurrentTime(audioRef.current.currentTime);
      }
    }, 250);

    return () => clearInterval(interval);
  }, [isPlaying]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const play = useCallback((url: string) => {
    // Same URL — just resume
    if (audioRef.current && currentSrc === url) {
      audioRef.current.play().catch(() => {});
      setIsPlaying(true);
      return;
    }

    // Tear down previous audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    const audio = new Audio(url);

    audio.addEventListener('loadedmetadata', () => {
      setDuration(audio.duration);
    });

    audio.addEventListener('ended', () => {
      setIsPlaying(false);
      setCurrentTime(0);
      onEndRef.current?.();
    });

    audioRef.current = audio;
    setCurrentSrc(url);
    setCurrentTime(0);
    setDuration(0);
    audio.play().catch(() => {});
    setIsPlaying(true);
  }, [currentSrc]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    setIsPlaying(false);
  }, []);

  const toggle = useCallback(() => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  }, [isPlaying]);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setCurrentTime(0);
    setCurrentSrc(null);
    setDuration(0);
  }, []);

  const seek = useCallback((time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  }, []);

  return { play, pause, toggle, stop, seek, isPlaying, currentTime, duration, currentSrc };
}
